/* ═══════════════════════════════════════════════════════════════
   DashboardViewsBar — saved-view switcher for the project dashboard's
   WidgetGrid `viewsBar` slot. Moved verbatim from the pre-R1
   dashboard-widgets module (dashboard-views-bar.tsx, Project Center R1
   T4a); only the shared-color import path changed (COLOR_* now live
   in pages/projects/widgets/shared.tsx).
   ═══════════════════════════════════════════════════════════════ */
import React from 'react';
import { Star, ArrowLeftRight, MoreHorizontal, Settings, Check } from 'lucide-react';
import type { DashboardView } from '../../../shared/entities';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/button';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from '../../components/ui/context-menu';
import { rem } from '../../lib/rem';
import { COLOR_OK, COLOR_DANGER, COLOR_INFO } from './widgets/shared';

export function DashboardViewsBar({
  views,
  activeViewId,
  currentUserId,
  onSwitch,
  onSaveAs,
  onSetDefault,
  onDelete,
  canSetFirmStarter,
  onSetFirmStarter,
  customizing,
  onToggleCustomize,
}: {
  views: DashboardView[];
  activeViewId: string | null;
  currentUserId: string;
  onSwitch: (id: string | null) => void;
  onSaveAs: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
  /** Gates the "Set as firm starter" overflow action (T4) —
   *  admin_center.manage_firm_settings; hidden otherwise. */
  canSetFirmStarter?: boolean;
  onSetFirmStarter?: () => void;
  /** R2-T2 — Customize lives on the views bar (it's about the view, not
   *  project details), same toggle/handler/gate the header used to own. */
  customizing?: boolean;
  onToggleCustomize?: () => void;
}) {
  const ownViews = views.filter((v) => v.userId === currentUserId);
  const sharedViews = views.filter((v) => v.userId !== currentUserId && v.shared);
  const activeView = views.find((v) => v.id === activeViewId);
  const isOwnerOfActive = activeView && activeView.userId === currentUserId;

  const chip = (active: boolean, accent: string): React.CSSProperties => ({
    padding: 'var(--space-1) var(--space-2)',
    minHeight: 'var(--space-6)',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${active ? accent : 'var(--bd)'}`,
    background: active ? `color-mix(in srgb, ${accent} 10%, transparent)` : 'transparent',
    color: active ? accent : 'var(--tx)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  });

  return (
    // Flat — part of the header chrome (Project Canvas verify fix-up): no
    // card bubble (no own border/radius/background/margin); the header
    // supplies the single hairline + surface + vertical rhythm.
    <div
      className="dashboard-views-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
      }}
    >
      <span style={sectionLabelInline}>View</span>
      <button
        onClick={() => onSwitch(null)}
        style={{
          ...chip(activeViewId === null, 'var(--ac)'),
          color: activeViewId === null ? 'var(--ac)' : 'var(--tm)',
        }}
      >
        Default
      </button>
      {ownViews.map((v) => {
        const active = v.id === activeViewId;
        return (
          <button key={v.id} onClick={() => onSwitch(v.id)} style={chip(active, 'var(--ac)')}>
            {v.name}
            {v.isDefault && (
              <span style={{ display: 'inline-flex', color: COLOR_OK }} title="Your default view">
                <Icon icon={Star} size="md" aria-hidden="true" />
              </span>
            )}
            {v.shared && (
              <span
                style={{ display: 'inline-flex', color: COLOR_INFO }}
                title="Shared with the firm"
              >
                <Icon icon={ArrowLeftRight} size="md" aria-hidden="true" />
              </span>
            )}
          </button>
        );
      })}
      {sharedViews.length > 0 && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            paddingLeft: 'var(--space-2)',
            borderLeft: '1px solid var(--bd)',
            marginLeft: 'var(--space-1)',
          }}
        >
          Firm shared
        </span>
      )}
      {sharedViews.map((v) => {
        const active = v.id === activeViewId;
        return (
          <button
            key={v.id}
            onClick={() => onSwitch(v.id)}
            title="Shared view (read-only)"
            style={chip(active, COLOR_INFO)}
          >
            <Icon icon={ArrowLeftRight} size="md" aria-hidden="true" /> {v.name}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      {isOwnerOfActive && (
        <>
          <button
            onClick={onSetDefault}
            style={{
              ...tinyBtn,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
            title="Make this view your default"
          >
            <Icon icon={Star} size="md" aria-hidden="true" /> Default
          </button>
          <button onClick={onDelete} style={{ ...tinyBtn, color: COLOR_DANGER }}>
            Delete
          </button>
        </>
      )}
      {canSetFirmStarter && onSetFirmStarter && (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <button
              aria-label="More view actions"
              title="More view actions"
              style={{
                ...tinyBtn,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 'var(--space-6)',
                minHeight: 'var(--space-6)',
              }}
            >
              <Icon icon={MoreHorizontal} size="md" aria-hidden="true" />
            </button>
          </ContextMenuTrigger>
          <ContextMenuContent
            align="end"
            sideOffset={4}
            className="rounded-md border border-border bg-surface p-1 shadow-md"
            style={{ minWidth: rem(220) }}
          >
            <ContextMenuItem onSelect={onSetFirmStarter}>Set as firm starter</ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      )}
      {onToggleCustomize && (
        <Button
          variant={customizing ? 'secondary' : 'outline'}
          size="sm"
          aria-pressed={customizing}
          onClick={onToggleCustomize}
          title="Rearrange, resize, hide, or add widgets"
        >
          {customizing ? (
            <>
              <Icon icon={Check} size="sm" /> Done customizing
            </>
          ) : (
            <>
              <Icon icon={Settings} size="sm" /> Customize
            </>
          )}
        </Button>
      )}
      <button onClick={onSaveAs} style={tinyBtnPrimary}>
        + Save current as view
      </button>
    </div>
  );
}

const tinyBtn: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-2)',
  minHeight: 'var(--space-6)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const tinyBtnPrimary: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-3)',
  minHeight: 'var(--space-6)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
};

const sectionLabelInline: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  letterSpacing: '.08em',
  textTransform: 'uppercase',
};
